import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    Typography,
    CircularProgress,
    Avatar,
    Collapse,
    Card,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { Refresh, AttachFile, Phone, Email, KeyboardArrowDown, KeyboardArrowUp, Close, Add, Update as UpdateIcon, ViewList, CalendarMonth } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import FlexBetween from "components/flexbox/FlexBetween";
import AppPaginationBar from "components/AppPaginationBar";
import FieldVisitCalendar from "page-sections/FieldVisit/FieldVisitCalendar";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import { Fragment, useEffect, useState } from "react";

const TABLE_CELL_PADDING = "12px 16px";
const FARMER_COLUMN_WIDTH = 170;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: TABLE_CELL_PADDING,
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.background.paper
            : theme.palette.grey[200],
        backgroundImage: "none",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    // Dark palette greys are inverted: low numbers = dark, high numbers = light.
    const hoverBg = isDark ? theme.palette.grey[300] : theme.palette.grey[100];
    const selectedBg = isDark ? theme.palette.grey[300] : theme.palette.grey[200];

    return {
        "&:hover": {
            backgroundColor: hoverBg,
        },
        "& .sticky-body-cell": {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: "none",
        },
        "&:hover .sticky-body-cell": {
            backgroundColor: hoverBg,
        },
        "&.Mui-selected": {
            backgroundColor: `${selectedBg} !important`,
        },
        "&.Mui-selected:hover": {
            backgroundColor: `${selectedBg} !important`,
        },
        "&.Mui-selected .sticky-body-cell, &.Mui-selected:hover .sticky-body-cell": {
            backgroundColor: selectedBg,
        },
    };
});

// ─── CHANGE 1: Destructure canViewImage from props ─────────────────────────────
const ASSIGNED_FIELD_VISIT_COLUMNS = [
    { key: "farmer_name", label: "Farmer Name" },
    { key: "farmer_contact", label: "Contact" },
    { key: "location", label: "Location" },
    { key: "visit_date", label: "Visit Date" },
    { key: "visit_purpose", label: "Visit Purpose" },
    { key: "remarks", label: "Remarks" },
    { key: "visit_status", label: "Visit Status" },
    { key: "assigned_to", label: "Assigned To" },
    { key: "assigned_by", label: "Assigned By" },
];

const ASSIGNED_VISIT_STATUS_OPTIONS = [
    "Upcoming",
    "Completed",
    "Rescheduled",
    "Cancelled",
];

const CUSTOMER_TYPE_OPTIONS = [
    "New Prospect",
    "Existing Farmer/Client",
];

const formatAssignedFieldValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") {
        if (value.name) return value.name;
        if (value.first_name || value.last_name) {
            return `${value.first_name || ""} ${value.last_name || ""}`.trim() || "-";
        }
        if (value.full_name) return value.full_name;
        if (value.id !== undefined) return String(value.id);
        return JSON.stringify(value);
    }
    return String(value);
};

const getUserDisplayName = (user) => {
    if (!user) return "";
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.full_name || user.name || user.email || `User ${user.id}`;
};

const getAssignedLocationDisplay = (visit) => {
    const village = formatAssignedFieldValue(visit?.village);
    if (village !== "-") return village;
    const parts = [
        visit?.tehsil,
        visit?.district,
        visit?.state,
        visit?.pincode,
        visit?.country,
    ]
        .map((part) => (part === null || part === undefined || part === "" ? null : String(part).trim()))
        .filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
};

const getAssignedLocationTooltip = (visit) => {
    const parts = [
        visit?.village,
        visit?.tehsil,
        visit?.district,
        visit?.state,
        visit?.pincode,
        visit?.country,
    ]
        .map((part) => (part === null || part === undefined || part === "" ? null : String(part).trim()))
        .filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
};

const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return "-";
    const raw = String(dateString).trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        const [, year, month, day] = match;
        return `${day}-${month}-${year}`;
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatDateTimeDisplay = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    return `${day} ${month} ${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const FieldVisitTable = ({
    fieldVisits,
    searchQuery,
    loading,
    onRefresh,
    pagination = null,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50, 100],
    canViewImage = true,
    canAssign = true,
    canUseCalendar = true,
    assignedFieldVisitMode = false,
    onStatusUpdate,
    onAssign,
    viewMode = "list",
    onViewModeChange,
    calendarVisits = [],
    calendarLoading = false,
}) => {
// ─────────────────────────────────────────────────────────────────────────────
    const theme = useTheme();
    const [openRow, setOpenRow] = useState(null);
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignForm, setAssignForm] = useState({
        assigned_to: "",
        farmer_name: "",
        farmer_contact: "",
        customer_type: "",
        village: "",
        tehsil: "",
        district: "",
        state: "",
        pincode: "",
        country: "India",
        visit_date: "",
        visit_purpose: "",
        remarks: "",
    });
    const [assignSubmitting, setAssignSubmitting] = useState(false);
    const [assignUsers, setAssignUsers] = useState([]);
    const [loadingAssignUsers, setLoadingAssignUsers] = useState(false);
    const [assignStates, setAssignStates] = useState([]);
    const [assignDistricts, setAssignDistricts] = useState([]);
    const [assignTalukas, setAssignTalukas] = useState([]);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [statusMenuVisit, setStatusMenuVisit] = useState(null);
    const [selectedVisitStatus, setSelectedVisitStatus] = useState("");
    const [statusRemarks, setStatusRemarks] = useState("");
    const [statusVisitDate, setStatusVisitDate] = useState("");
    const [updatingVisitStatus, setUpdatingVisitStatus] = useState(false);
    const SR_NO_COLUMN_WIDTH = 58;
    const VISIT_DATE_COLUMN_WIDTH = 120;
    const VISIT_DATE_STICKY_LEFT = SR_NO_COLUMN_WIDTH;
    const FARMER_NAME_STICKY_LEFT = VISIT_DATE_STICKY_LEFT + VISIT_DATE_COLUMN_WIDTH;
    const handleRowClick = (rowId) => {
        setOpenRow((prev) => (prev === rowId ? null : rowId));
    };

    useEffect(() => {
        const fetchAssignUsers = async () => {
            setLoadingAssignUsers(true);
            try {
                const res = await apiService.get(
                    `m2/user/get-users-by-type/?type_name=${encodeURIComponent("Field Excutive")}`
                );
                const list =
                    res?.data?.data ||
                    res?.data?.users ||
                    res?.data?.Users ||
                    (Array.isArray(res?.data) ? res.data : []) ||
                    [];
                const active = Array.isArray(list) ? list.filter((u) => !u.is_deleted) : [];
                const uniqueById = [];
                const seenIds = new Set();
                const seenNames = new Set();
                active.forEach((user) => {
                    const idKey = String(user?.id ?? "");
                    const nameKey = getUserDisplayName(user).trim().toLowerCase();
                    if (idKey && seenIds.has(idKey)) return;
                    if (nameKey && seenNames.has(nameKey)) return;
                    if (idKey) seenIds.add(idKey);
                    if (nameKey) seenNames.add(nameKey);
                    uniqueById.push(user);
                });
                setAssignUsers(uniqueById);
            } catch (error) {
                console.error("Error fetching users for assign:", error);
                setAssignUsers([]);
            } finally {
                setLoadingAssignUsers(false);
            }
        };

        fetchAssignUsers();
    }, []);

    const fetchAssignStates = async (countryName) => {
        if (!countryName) return;
        try {
            const res = await apiService.get(
                `${API_ENDPOINTS.GET_STATES}?country_name=${encodeURIComponent(countryName)}`
            );
            const options = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data)
                    ? res.data
                    : [];
            setAssignStates(options);
        } catch (error) {
            console.error("Failed to fetch states:", error);
            setAssignStates([]);
        }
    };

    const fetchAssignDistricts = async (stateName) => {
        if (!stateName) return;
        try {
            const res = await apiService.get(
                `${API_ENDPOINTS.GET_DISTRICTS}?state=${encodeURIComponent(stateName)}`
            );
            const options = Array.isArray(res?.data?.District)
                ? res.data.District
                : Array.isArray(res?.data?.data)
                    ? res.data.data
                    : [];
            setAssignDistricts(options);
        } catch (error) {
            console.error("Failed to fetch districts:", error);
            setAssignDistricts([]);
        }
    };

    const fetchAssignTalukas = async (districtName) => {
        if (!districtName) return;
        try {
            const res = await apiService.get(
                `${API_ENDPOINTS.GET_TALUKA}?district=${encodeURIComponent(districtName)}`
            );
            const options = Array.isArray(res?.data?.Taluka)
                ? res.data.Taluka
                : Array.isArray(res?.data?.data)
                    ? res.data.data
                    : [];
            setAssignTalukas(options);
        } catch (error) {
            console.error("Failed to fetch talukas:", error);
            setAssignTalukas([]);
        }
    };

    useEffect(() => {
        if (!assignDialogOpen) return;
        fetchAssignStates(assignForm.country || "India");
    }, [assignDialogOpen]);

    const getAssignedToDisplayName = (assignedTo) => {
        if (assignedTo === null || assignedTo === undefined || assignedTo === "") return "-";
        if (typeof assignedTo === "object") return formatAssignedFieldValue(assignedTo);
        const matchedUser = assignUsers.find(
            (user) => String(user.id) === String(assignedTo)
        );
        return matchedUser ? getUserDisplayName(matchedUser) : String(assignedTo);
    };

    const getAssignedByDisplayName = (assignedBy) => {
        if (assignedBy === null || assignedBy === undefined || assignedBy === "") return "-";
        if (typeof assignedBy === "object") {
            return (
                formatAssignedFieldValue(assignedBy) ||
                getUserDisplayName(assignedBy) ||
                "-"
            );
        }
        const matchedUser = assignUsers.find(
            (user) => String(user.id) === String(assignedBy)
        );
        if (matchedUser) return getUserDisplayName(matchedUser);
        // Backend may already send a name string
        if (Number.isNaN(Number(assignedBy))) return String(assignedBy);
        return String(assignedBy);
    };

    const filteredFieldVisits = fieldVisits.filter(visit => {
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            
            const productSubcategoryMatch = visit.product_subcategory?.some(product => 
                product.name?.toLowerCase().includes(searchLower) ||
                product.sku?.toLowerCase().includes(searchLower)
            );
            
            const cropMatch = visit.crop?.name?.toLowerCase().includes(searchLower) ||
                (typeof visit.crop === 'string' && visit.crop.toLowerCase().includes(searchLower));
            
            const matchesSearch = (
                visit.farmer_name?.toLowerCase().includes(searchLower) ||
                visit.village?.toLowerCase().includes(searchLower) ||
                visit.district?.toLowerCase().includes(searchLower) ||
                visit.executive_name?.toLowerCase().includes(searchLower) ||
                visit.farmer_email?.toLowerCase().includes(searchLower) ||
                visit.farmer_contact?.includes(searchLower) ||
                visit.tehsil?.toLowerCase().includes(searchLower) ||
                visit.state?.toLowerCase().includes(searchLower) ||
                visit.country?.toLowerCase().includes(searchLower) ||
                visit.visit_purpose?.toLowerCase().includes(searchLower) ||
                visit.remarks?.toLowerCase().includes(searchLower) ||
                visit.visit_status?.toLowerCase().includes(searchLower) ||
                String(visit.assigned_to ?? "").includes(searchLower) ||
                visit.product_vertical?.name?.toLowerCase().includes(searchLower) ||
                cropMatch ||
                productSubcategoryMatch
            );
            
            if (!matchesSearch) return false;
        }
        
        return true;
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getInitials = (name) => {
        if (!name) return 'N/A';
        const nameParts = name.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    const handleFileView = (fileUrl) => {
        if (fileUrl) {
            const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_ENDPOINTS.BASE_URL || ''}${fileUrl}`;
            setSelectedImageUrl(fullUrl);
            setOpenImageDialog(true);
        }
    };

    const handleCloseImageDialog = () => {
        setOpenImageDialog(false);
        setSelectedImageUrl('');
    };

    const renderShowingText = () => {
        if (pagination?.total_items != null) {
            if (pagination.total_items > 0) {
                const start = (pagination.current_page - 1) * pagination.page_size + 1;
                const end = Math.min(pagination.current_page * pagination.page_size, pagination.total_items);
                return `Showing ${start} - ${end} of ${pagination.total_items}`;
            }
            return "Total: 0 visits";
        }

        const total = filteredFieldVisits.length;
        return `Total: ${total} ${total === 1 ? "visit" : "visits"}`;
    };

    const handleAssignSubmit = async () => {
        if (!onAssign) return;
        if (!assignForm.assigned_to) return;
        if (!String(assignForm.farmer_name || "").trim()) return;
        if (!/^\d{10}$/.test(String(assignForm.farmer_contact || "").trim())) return;
        if (!String(assignForm.customer_type || "").trim()) return;
        if (!String(assignForm.country || "").trim()) return;
        if (!String(assignForm.state || "").trim()) return;
        if (!String(assignForm.district || "").trim()) return;
        if (!String(assignForm.tehsil || "").trim()) return;
        if (!String(assignForm.village || "").trim()) return;
        if (!/^\d{6}$/.test(String(assignForm.pincode || "").trim())) return;
        if (!String(assignForm.visit_date || "").trim()) return;
        if (!String(assignForm.visit_purpose || "").trim()) return;
        setAssignSubmitting(true);
        try {
            await onAssign({
                ...assignForm,
                assigned_to: Number(assignForm.assigned_to),
            });
            setAssignDialogOpen(false);
            setAssignForm({
                assigned_to: "",
                farmer_name: "",
                farmer_contact: "",
                customer_type: "",
                village: "",
                tehsil: "",
                district: "",
                state: "",
                pincode: "",
                country: "India",
                visit_date: "",
                visit_purpose: "",
                remarks: "",
            });
            setAssignDistricts([]);
            setAssignTalukas([]);
        } finally {
            setAssignSubmitting(false);
        }
    };

    if (assignedFieldVisitMode) {
    return (
        <Box sx={{ width: "100%" }}>
        <Card sx={{ width: "100%", p: 2 }}>
            <FlexBetween mb={2} px={1}>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            {/* <Typography variant="h6" fontWeight={600}>
                                Assign Field Visit
                            </Typography> */}
                            {onViewModeChange && (
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={viewMode}
                                    onChange={(_, value) => {
                                        if (value) onViewModeChange(value);
                                    }}
                                    sx={{
                                        bgcolor: "#fff",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        "& .MuiToggleButton-root": {
                                            textTransform: "none",
                                            px: 1.5,
                                            py: 0.6,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            border: "none",
                                            color: "text.secondary",
                                            gap: 0.75,
                                            "&.Mui-selected": {
                                                bgcolor: "primary.main",
                                                color: "#fff",
                                                "&:hover": { bgcolor: "primary.dark" },
                                            },
                                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                                        },
                                    }}
                                >
                                    <ToggleButton value="list">
                                        <ViewList sx={{ fontSize: 16 }} />
                                        List
                                    </ToggleButton>
                                    <ToggleButton value="calendar" disabled={!canUseCalendar}>
                                        <CalendarMonth sx={{ fontSize: 16 }} />
                                        Calendar
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            {viewMode === "list" && pagination && onPageSizeChange && (
                                <FormControl size="small" sx={{ minWidth: 110 }}>
                                    <InputLabel>Rows</InputLabel>
                                    <Select
                                        value={pagination.page_size}
                                        label="Rows"
                                        onChange={onPageSizeChange}
                                    >
                                        {pageSizeOptions.map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {onAssign && (
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={() => canAssign && setAssignDialogOpen(true)}
                                    disabled={!canAssign}
                                    sx={{
                                        borderRadius: 2,
                                        height: 40,
                                        boxShadow: "none",
                                        opacity: !canAssign ? 0.6 : 1,
                                    }}
                                >
                                    Assign Field Visit
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={onRefresh}
                                disabled={loading || calendarLoading}
                                startIcon={<Refresh />}
                                sx={{ borderRadius: 2, height: 40, boxShadow: "none" }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </FlexBetween>

                    {viewMode === "calendar" ? (
                        <FieldVisitCalendar
                            visits={calendarVisits}
                            loading={calendarLoading}
                        />
                    ) : loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ ml: 2 }}>
                                Loading field visits...
                            </Typography>
                        </Box>
                    ) : filteredFieldVisits.length > 0 ? (
                        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell className="header" align="center">
                                            Sr. No.
                                        </StyledTableCell>
                                        {ASSIGNED_FIELD_VISIT_COLUMNS.map((col) => (
                                            <StyledTableCell key={col.key} className="header" align="center">
                                                {col.label}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredFieldVisits.map((visit, index) => {
                                        const isOpen = openRow === visit.id;
                                        return (
                                            <Fragment key={visit.id ?? index}>
                                                <StyledTableRow
                                                    selected={isOpen}
                                                    onClick={() => handleRowClick(visit.id)}
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <StyledTableCell align="center">
                                                        {pagination?.current_page && pagination?.page_size
                                                            ? (pagination.current_page - 1) * pagination.page_size + (index + 1)
                                                            : index + 1}
                                                    </StyledTableCell>
                                                    {ASSIGNED_FIELD_VISIT_COLUMNS.map((col) => (
                                                        <StyledTableCell key={col.key} align="center">
                                                            {col.key === "assigned_to" ? (
                                                                getAssignedToDisplayName(visit.assigned_to)
                                                            ) : col.key === "assigned_by" ? (
                                                                getAssignedByDisplayName(
                                                                    visit.assigned_by ??
                                                                    visit.assigned_by_name ??
                                                                    visit.assigned_by_user
                                                                )
                                                            ) : col.key === "location" ? (
                                                                <Tooltip
                                                                    title={getAssignedLocationTooltip(visit)}
                                                                    arrow
                                                                    placement="bottom"
                                                                    componentsProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                bgcolor: "#404E67",
                                                                                color: "#fff",
                                                                                fontSize: "0.75rem",
                                                                                fontWeight: 400,
                                                                                px: 1.25,
                                                                                py: 0.75,
                                                                                borderRadius: 1,
                                                                                maxWidth: 320,
                                                                                textAlign: "center",
                                                                            },
                                                                        },
                                                                        arrow: {
                                                                            sx: {
                                                                                color: "#404E67",
                                                                            },
                                                                        },
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            maxWidth: 150,
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                            cursor: "help",
                                                                            textAlign: "center",
                                                                            mx: "auto",
                                                                        }}
                                                                    >
                                                                        {getAssignedLocationDisplay(visit)}
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : col.key === "visit_date" ? (
                                                                formatDateDDMMYYYY(visit.visit_date)
                                                            ) : (
                                                                formatAssignedFieldValue(visit[col.key])
                                                            )}
                                                        </StyledTableCell>
                                                    ))}
                                                </StyledTableRow>
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={ASSIGNED_FIELD_VISIT_COLUMNS.length + 1}
                                                        sx={{ p: 0, border: 0 }}
                                                    >
                                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                            <Card sx={{ m: 1, p: 2 }}>
                                                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                                                    Field Visit Details
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: {
                                                                            xs: "1fr",
                                                                            sm: "1fr 1fr",
                                                                            md: "1fr 1fr 1fr",
                                                                        },
                                                                        gap: 2,
                                                                    }}
                                                                >
                                                                    {[
                                                                        { label: "Farmer Name", value: formatAssignedFieldValue(visit.farmer_name) },
                                                                        { label: "Farmer Contact", value: formatAssignedFieldValue(visit.farmer_contact) },
                                                                        { label: "Customer Type", value: formatAssignedFieldValue(visit.customer_type) },
                                                                        { label: "Location", value: getAssignedLocationTooltip(visit) },
                                                                        { label: "Country", value: formatAssignedFieldValue(visit.country) },
                                                                        { label: "Visit Date", value: formatDateDDMMYYYY(visit.visit_date) },
                                                                        { label: "Visit Purpose", value: formatAssignedFieldValue(visit.visit_purpose) },
                                                                        { label: "Remarks", value: formatAssignedFieldValue(visit.remarks) },
                                                                        { label: "Visit Status", value: formatAssignedFieldValue(visit.visit_status) },
                                                                        { label: "Assigned To", value: getAssignedToDisplayName(visit.assigned_to) },
                                                                        { label: "Assigned By", value: getAssignedByDisplayName(
                                                                            visit.assigned_by ??
                                                                            visit.assigned_by_name ??
                                                                            visit.assigned_by_user
                                                                        ) },
                                                                        { label: "Created At", value: formatDateTimeDisplay(visit.created_at) },
                                                                        { label: "Updated At", value: formatDateTimeDisplay(visit.updated_at) },
                                                                    ].map((field) => (
                                                                        <Box key={field.label}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                                                                                {field.label}
                                                                            </Typography>
                                                                            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: "break-word" }}>
                                                                                {field.value}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </Card>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box py={6} textAlign="center">
                            <Typography color="text.secondary">No field visit records found</Typography>
                        </Box>
                    )}

                    {viewMode === "list" && (
                        <AppPaginationBar
                            pagination={
                                searchQuery
                                    ? filteredFieldVisits.length > 0
                                        ? {
                                            ...pagination,
                                            total_items: filteredFieldVisits.length,
                                            total_pages: Math.ceil(filteredFieldVisits.length / (pagination?.page_size || 10)),
                                            current_page: 1,
                                        }
                                        : null
                                    : pagination
                            }
                            onPageChange={onPageChange}
                            itemLabel="field visits"
                        />
                    )}
                </Card>

                <Dialog
                    open={statusDialogOpen}
                    onClose={() => {
                        if (updatingVisitStatus) return;
                        setStatusDialogOpen(false);
                        setStatusMenuVisit(null);
                        setSelectedVisitStatus("");
                        setStatusRemarks("");
                        setStatusVisitDate("");
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <UpdateIcon color="primary" />
                            <Typography variant="h6">Update Visit Status</Typography>
                        </Box>
                        {statusMenuVisit && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Visit ID: {statusMenuVisit.id}
                            </Typography>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Visit Status</InputLabel>
                            <Select
                                value={selectedVisitStatus}
                                label="Visit Status"
                                onChange={(e) => setSelectedVisitStatus(e.target.value)}
                            >
                                {ASSIGNED_VISIT_STATUS_OPTIONS.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedVisitStatus === "Rescheduled" && (
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="New Visit Date"
                                type="date"
                                value={statusVisitDate}
                                onChange={(e) => setStatusVisitDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Calendar and reminder will use this new date"
                                sx={{ mt: 2 }}
                            />
                        )}
                        {(selectedVisitStatus === "Cancelled" || selectedVisitStatus === "Rescheduled") && (
                            <TextField
                                fullWidth
                                size="small"
                                label="Remarks"
                                multiline
                                minRows={2}
                                value={statusRemarks}
                                onChange={(e) => setStatusRemarks(e.target.value)}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                if (updatingVisitStatus) return;
                                setStatusDialogOpen(false);
                                setStatusMenuVisit(null);
                                setSelectedVisitStatus("");
                                setStatusRemarks("");
                                setStatusVisitDate("");
                            }}
                            disabled={updatingVisitStatus}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={
                                updatingVisitStatus ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <UpdateIcon />
                                )
                            }
                            disabled={
                                updatingVisitStatus ||
                                !selectedVisitStatus ||
                                !statusMenuVisit?.id ||
                                !onStatusUpdate ||
                                (selectedVisitStatus === "Rescheduled" && !statusVisitDate)
                            }
                            onClick={async () => {
                                if (!statusMenuVisit?.id || !selectedVisitStatus || !onStatusUpdate) return;
                                if (selectedVisitStatus === "Rescheduled" && !statusVisitDate) return;
                                setUpdatingVisitStatus(true);
                                try {
                                    const needsRemarks =
                                        selectedVisitStatus === "Cancelled" ||
                                        selectedVisitStatus === "Rescheduled";
                                    await onStatusUpdate(
                                        statusMenuVisit.id,
                                        selectedVisitStatus,
                                        needsRemarks ? statusRemarks : undefined,
                                        selectedVisitStatus === "Rescheduled" ? statusVisitDate : undefined
                                    );
                                    setStatusDialogOpen(false);
                                    setStatusMenuVisit(null);
                                    setSelectedVisitStatus("");
                                    setStatusRemarks("");
                                    setStatusVisitDate("");
                                } finally {
                                    setUpdatingVisitStatus(false);
                                }
                            }}
                        >
                            {updatingVisitStatus ? "Updating Status..." : "Update Status"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Field Visit</DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Assigned To"
                                value={assignForm.assigned_to}
                                onChange={(e) => {
                                    setAssignForm((prev) => ({ ...prev, assigned_to: e.target.value }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => {
                                        if (!selected) {
                                            return (
                                                <Typography component="span" sx={{ color: "text.secondary" }}>
                                                    {loadingAssignUsers ? "Loading users..." : "Select user"}
                                                </Typography>
                                            );
                                        }
                                        const selectedUser = assignUsers.find(
                                            (user) => String(user.id) === String(selected)
                                        );
                                        return selectedUser
                                            ? getUserDisplayName(selectedUser)
                                            : selected;
                                    },
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 320,
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    {loadingAssignUsers ? "Loading users..." : "Select user"}
                                </MenuItem>
                                {assignUsers.map((user) => (
                                    <MenuItem key={user.id} value={String(user.id)}>
                                        {getUserDisplayName(user)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Farmer Name"
                                value={assignForm.farmer_name}
                                onChange={(e) => {
                                    const lettersOnly = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                    setAssignForm((prev) => ({ ...prev, farmer_name: lettersOnly }));
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Farmer Contact"
                                value={assignForm.farmer_contact}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setAssignForm((prev) => ({ ...prev, farmer_contact: digitsOnly }));
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                                error={
                                    String(assignForm.farmer_contact || "").length > 0 &&
                                    !/^\d{10}$/.test(String(assignForm.farmer_contact || ""))
                                }
                                helperText={
                                    String(assignForm.farmer_contact || "").length > 0 &&
                                    !/^\d{10}$/.test(String(assignForm.farmer_contact || ""))
                                        ? "Farmer Contact must be exactly 10 digits"
                                        : undefined
                                }
                            />
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Customer Type"
                                value={assignForm.customer_type}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, customer_type: e.target.value }))
                                }
                            >
                                <MenuItem value="" disabled>
                                    Select customer type
                                </MenuItem>
                                {CUSTOMER_TYPE_OPTIONS.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Country"
                                value={assignForm.country}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        country: value,
                                        state: "",
                                        district: "",
                                        tehsil: "",
                                    }));
                                    setAssignStates([]);
                                    setAssignDistricts([]);
                                    setAssignTalukas([]);
                                    if (value) fetchAssignStates(value);
                                }}
                            />
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="State"
                                value={assignForm.state}
                                onChange={(e) => {
                                    const selectedState = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        state: selectedState,
                                        district: "",
                                        tehsil: "",
                                    }));
                                    setAssignDistricts([]);
                                    setAssignTalukas([]);
                                    fetchAssignDistricts(selectedState);
                                }}
                            >
                                {assignStates.length > 0 ? (
                                    assignStates.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a country first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="District"
                                value={assignForm.district}
                                onChange={(e) => {
                                    const selectedDistrict = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        district: selectedDistrict,
                                        tehsil: "",
                                    }));
                                    setAssignTalukas([]);
                                    fetchAssignTalukas(selectedDistrict);
                                }}
                            >
                                {assignDistricts.length > 0 ? (
                                    assignDistricts.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a state first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Tahshil"
                                value={assignForm.tehsil}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, tehsil: e.target.value }))
                                }
                            >
                                {assignTalukas.length > 0 ? (
                                    assignTalukas.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a district first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Village"
                                value={assignForm.village}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, village: e.target.value }))
                                }
                            />
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Pincode"
                                value={assignForm.pincode}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    setAssignForm((prev) => ({ ...prev, pincode: digitsOnly }));
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                error={
                                    String(assignForm.pincode || "").length > 0 &&
                                    !/^\d{6}$/.test(String(assignForm.pincode || ""))
                                }
                                helperText={
                                    String(assignForm.pincode || "").length > 0 &&
                                    !/^\d{6}$/.test(String(assignForm.pincode || ""))
                                        ? "Pincode must be exactly 6 digits"
                                        : undefined
                                }
                            />
                            {[
                                ["visit_date", "Visit Date", "date"],
                                ["visit_purpose", "Visit Purpose", "text"],
                                ["remarks", "Remarks", "text"],
                            ].map(([field, label, type]) => (
                                <TextField
                                    key={field}
                                    fullWidth
                                    size="small"
                                    required={field !== "remarks"}
                                    label={label}
                                    type={type}
                                    value={assignForm[field]}
                                    onChange={(e) =>
                                        setAssignForm((prev) => ({ ...prev, [field]: e.target.value }))
                                    }
                                    InputLabelProps={type === "date" ? { shrink: true } : undefined}
                                    inputProps={
                                        field === "visit_date"
                                            ? { min: new Date().toISOString().slice(0, 10) }
                                            : undefined
                                    }
                                />
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleAssignSubmit}
                            disabled={
                                assignSubmitting ||
                                !assignForm.assigned_to ||
                                !String(assignForm.farmer_name || "").trim() ||
                                !/^\d{10}$/.test(String(assignForm.farmer_contact || "").trim()) ||
                                !String(assignForm.customer_type || "").trim() ||
                                !String(assignForm.country || "").trim() ||
                                !String(assignForm.state || "").trim() ||
                                !String(assignForm.district || "").trim() ||
                                !String(assignForm.tehsil || "").trim() ||
                                !String(assignForm.village || "").trim() ||
                                !/^\d{6}$/.test(String(assignForm.pincode || "").trim()) ||
                                !String(assignForm.visit_date || "").trim() ||
                                !String(assignForm.visit_purpose || "").trim()
                            }
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                            {assignSubmitting ? "Assigning..." : "Assign To"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
        <Card sx={{ width: "100%", p: 2 }}>
            <FlexBetween mb={2} px={1}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    {onViewModeChange ? (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={viewMode}
                            onChange={(_, value) => {
                                if (value) onViewModeChange(value);
                            }}
                            sx={{
                                bgcolor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                overflow: "hidden",
                                "& .MuiToggleButton-root": {
                                    textTransform: "none",
                                    px: 1.5,
                                    py: 0.6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    border: "none",
                                    color: "text.secondary",
                                    gap: 0.75,
                                    "&.Mui-selected": {
                                        bgcolor: "primary.main",
                                        color: "#fff",
                                        "&:hover": { bgcolor: "primary.dark" },
                                    },
                                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                                },
                            }}
                        >
                            <ToggleButton value="list">
                                <ViewList sx={{ fontSize: 16 }} />
                                List
                            </ToggleButton>
                            <ToggleButton value="calendar" disabled={!canUseCalendar}>
                                <CalendarMonth sx={{ fontSize: 16 }} />
                                Calendar
                            </ToggleButton>
                        </ToggleButtonGroup>
                    ) : (
                <Typography variant="h6" fontWeight={600}>
                    Field Visit Records
                </Typography>
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {viewMode === "list" && pagination && onPageSizeChange && (
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Rows</InputLabel>
                        <Select
                          value={pagination.page_size}
                          label="Rows"
                          onChange={onPageSizeChange}
                        >
                          {pageSizeOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {onAssign && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => canAssign && setAssignDialogOpen(true)}
                            disabled={!canAssign}
                            sx={{
                                borderRadius: 2,
                                height: 40,
                                boxShadow: "none",
                                opacity: !canAssign ? 0.6 : 1,
                            }}
                        >
                            Assign Field Visit
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={onRefresh}
                        disabled={loading || calendarLoading}
                        startIcon={<Refresh />}
                        sx={{ 
                            minWidth: "auto",
                            px: 2,
                            borderRadius: 2,
                            height: 40,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </FlexBetween>

            {viewMode === "calendar" && onViewModeChange ? (
                <FieldVisitCalendar
                    visits={calendarVisits}
                    loading={calendarLoading}
                />
            ) : loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>Loading field visits...</Typography>
                </Box>
            ) : filteredFieldVisits.length > 0 ? (
                <TableContainer component={Paper} sx={{ position: 'relative', overflowX: 'auto', scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            }, }}>
                    <Table stickyHeader>
                        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 50 }}>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                                <StyledTableCell
                                    className="header"
                                    align="center"
                                    sx={{
                                        padding: "12px 4px",
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 60,
                                        width: SR_NO_COLUMN_WIDTH,
                                        minWidth: SR_NO_COLUMN_WIDTH,
                                        textAlign: 'center',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Sr. No.
                                </StyledTableCell>
                                <StyledTableCell
                                    className="header"
                                    sx={{
                                        padding: "12px 6px",
                                        position: 'sticky',
                                        left: VISIT_DATE_STICKY_LEFT,
                                        zIndex: 59,
                                        width: VISIT_DATE_COLUMN_WIDTH,
                                        minWidth: VISIT_DATE_COLUMN_WIDTH,
                                        textAlign: 'center',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Visit Date
                                </StyledTableCell>
                                <StyledTableCell
                                    className="header"
                                    sx={{
                                        position: 'sticky',
                                        left: FARMER_NAME_STICKY_LEFT,
                                        zIndex: 58,
                                        width: FARMER_COLUMN_WIDTH,
                                        minWidth: FARMER_COLUMN_WIDTH,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[200],
                                    }}
                                >
                                    Farmer Details
                                </StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Location</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Contact</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Product Vertical</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Crop</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Products</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Farm Area</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Plantation Date</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Observations</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Recommendations</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Visit Purpose</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Visit Status</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Assigned To</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Executive</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Upload File</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ textAlign: "center" }}>Added By</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredFieldVisits.map((visit, index) => {
                                const rowKey = `${visit.__isAssigned ? "assigned" : "field"}-${visit.id}`;
                                const isOpen = openRow === rowKey;
                                const stickyCellBg = isOpen
                                    ? (theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[200])
                                    : theme.palette.background.paper;
                                return (
                                    <Fragment key={`${rowKey}-${index}`}>
                                        <StyledTableRow
                                          selected={isOpen}
                                          onClick={() => handleRowClick(rowKey)}
                                          sx={{ cursor: "pointer" }}
                                        >
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        align="center"
                                        sx={{
                                            padding: "12px 4px",
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 34,
                                            width: SR_NO_COLUMN_WIDTH,
                                            minWidth: SR_NO_COLUMN_WIDTH,
                                            textAlign: 'center',
                                            backgroundColor: stickyCellBg,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                            {pagination && pagination.current_page && pagination.page_size
                                                ? (pagination.current_page - 1) * pagination.page_size + (index + 1)
                                                : index + 1}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            padding: "12px 6px",
                                            position: 'sticky',
                                            left: VISIT_DATE_STICKY_LEFT,
                                            zIndex: 33,
                                            width: VISIT_DATE_COLUMN_WIDTH,
                                            minWidth: VISIT_DATE_COLUMN_WIDTH,
                                            textAlign: 'center',
                                            backgroundColor: stickyCellBg,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} width="100%">
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setOpenRow(isOpen ? null : rowKey);
                                                }}
                                                onDoubleClick={(event) => event.stopPropagation()}
                                            >
                                                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                            <Typography variant="body2" fontWeight={500}>
                                                {formatDate(visit.visit_date)}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            position: 'sticky',
                                            left: FARMER_NAME_STICKY_LEFT,
                                            zIndex: 32,
                                            width: FARMER_COLUMN_WIDTH,
                                            minWidth: FARMER_COLUMN_WIDTH,
                                            backgroundColor: stickyCellBg,
                                            backgroundImage: 'none',
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36,
                                                    bgcolor: theme.palette.primary.main,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getInitials(visit.farmer_name)}
                                            </Avatar>
                                            <Box>
                                                <Tooltip title={visit.farmer_name || 'N/A'} arrow>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        fontWeight={600}
                                                        sx={{ 
                                                            maxWidth: 150, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        {visit.farmer_name || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip 
                                            title={`${visit.village || ''}${visit.tehsil ? `, ${visit.tehsil}` : ''}\n${visit.district || ''}, ${visit.state || ''}`} 
                                            arrow
                                        >
                                            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                        textAlign: 'center',
                                                        mx: 'auto'
                                                    }}
                                                >
                                                    {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                        display: 'block',
                                                        textAlign: 'center',
                                                        mx: 'auto'
                                                    }}
                                                >
                                                    {visit.district}, {visit.state}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5} justifyContent="center">
                                                <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="body2">
                                                    {visit.farmer_contact || 'N/A'}
                                                </Typography>
                                            </Box>
                                            {visit.farmer_email && (
                                                <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                                                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Tooltip title={visit.farmer_email} arrow>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                maxWidth: 150, 
                                                                overflow: 'hidden', 
                                                                textOverflow: 'ellipsis', 
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'help'
                                                            }}
                                                        >
                                                            {visit.farmer_email}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <Chip 
                                                label={visit.company_vertical?.name || 'N/A'} 
                                                size="small" 
                                                color="success"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        {visit.crop ? (
                                            <Chip 
                                                label={visit.crop.name || visit.crop} 
                                                size="small" 
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                N/A
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                                {visit.product_subcategory.map((product, index) => (
                                                    <Box key={product.id || index} sx={{ textAlign: 'center', width: '100%' }}>
                                                        <Tooltip title={product.name || '-'} arrow>
                                                            <Typography 
                                                                variant="body2" 
                                                                fontWeight={500}
                                                                sx={{ 
                                                                    maxWidth: 200, 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap',
                                                                    cursor: 'help',
                                                                    textAlign: 'center',
                                                                    mx: 'auto'
                                                                }}
                                                            >
                                                                {product.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="center">
                                                            {product.sku && (
                                                                <Chip 
                                                                    label={`SKU: ${product.sku}`} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No products
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {visit.farm_area_value 
                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {formatDate(visit.plantation_date)}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip title={visit.observations || '-'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.observations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip title={visit.recommendations || '-'} arrow>
                                            <Typography 
                                                variant="body2"
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.recommendations || '-'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>

                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip
                                            title={formatAssignedFieldValue(
                                                visit.visit_purpose ?? visit.purpose
                                            )}
                                            arrow
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {formatAssignedFieldValue(
                                                    visit.visit_purpose ?? visit.purpose
                                                )}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>

                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {formatAssignedFieldValue(
                                                visit.visit_status ?? visit.status
                                            )}
                                        </Typography>
                                    </StyledTableCell>

                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {getAssignedToDisplayName(
                                                visit.assigned_to ??
                                                    visit.assigned_to_name ??
                                                    visit.assigned_to_user
                                            )}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip title={visit.executive_name || 'N/A'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                sx={{ 
                                                    maxWidth: 120, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {visit.executive_name || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    {/* ─── CHANGE 2: View Image button — disabled when no view_image permission ─ */}
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        {visit.upload_file ? (
                                            <Tooltip title={canViewImage ? "View Image" : "No permission to view image"}>
                                                <span>
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            if (canViewImage) {
                                                                handleFileView(visit.upload_file);
                                                            }
                                                        }}
                                                        disabled={!canViewImage}
                                                    >
                                                        <AttachFile fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No file
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    {/* ─────────────────────────────────────────────────────────────────────── */}
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Box sx={{ textAlign: "center" }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {visit.added_by?.first_name} {visit.added_by?.last_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {visit.added_by?.email}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>

                                <TableRow>
                                    <TableCell colSpan={18} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Card
                                                sx={(theme) => ({
                                                    m: 1,
                                                    p: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? alpha(theme.palette.background.paper, 0.8)
                                                        : alpha(theme.palette.primary.main, 0.02),
                                                    borderRadius: 2,
                                                })}
                                            >
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight={600} 
                                                    sx={{ mb: 2, textDecoration: 'underline' }}
                                                >
                                                    Field Visit Details - #{visit.id}
                                                </Typography>

                                                {visit.product_subcategory && visit.product_subcategory.length > 0 ? (
                                                    <>
                                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                                            Products
                                                        </Typography>
                                                        <Table size="small" sx={{ mb: 2, width: 'auto' }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 600, pr: 2 }}>Product Name</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600, pl: 21 }}>SKU</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {visit.product_subcategory.map((product, index) => (
                                                                    <TableRow
                                                                        key={product.id || index}
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                                            },
                                                                        }}
                                                                    >
                                                                        <TableCell sx={{ pr: 8 }}>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {product.name}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell sx={{ pl: 21 }}>{product.sku || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        No products listed
                                                    </Typography>
                                                )}

                                                <Divider sx={{ my: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        flexWrap: 'wrap',
                                                        columnGap: 8,
                                                        rowGap: 3,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Visit Date:</strong> {formatDate(visit.visit_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Plantation Date:</strong> {formatDate(visit.plantation_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farm Area:</strong> {visit.farm_area_value 
                                                                ? `${visit.farm_area_value} ${visit.farm_area_unit || ''}`
                                                                : 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Product Vertical:</strong> {visit.product_vertical?.name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Crop:</strong> {visit.crop?.name || visit.crop || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farmer Name:</strong> {visit.farmer_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Contact:</strong> {visit.farmer_contact || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Email:</strong> {visit.farmer_email || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Location:</strong> {visit.village}{visit.tehsil ? `, ${visit.tehsil}` : ''}, {visit.district}, {visit.state}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Executive:</strong> {visit.executive_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Visit Purpose:</strong>{" "}
                                                            {formatAssignedFieldValue(
                                                                visit.visit_purpose ?? visit.purpose
                                                            )}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Visit Status:</strong>{" "}
                                                            {formatAssignedFieldValue(
                                                                visit.visit_status ?? visit.status
                                                            )}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Assigned To:</strong>{" "}
                                                            {getAssignedToDisplayName(
                                                                visit.assigned_to ??
                                                                    visit.assigned_to_name ??
                                                                    visit.assigned_to_user
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ maxWidth: '300px' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Observations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.observations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Recommendations:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                            {visit.recommendations || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Feedback:</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {visit.farmer_feedback || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Total Products:</strong> {visit.product_subcategory?.length || 0}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Added By:</strong> {visit.added_by?.first_name} {visit.added_by?.last_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                    }}
                >
                    <Typography 
                        variant="h6" 
                        color="text.secondary"
                        align="center"
                    >
                        {searchQuery 
                            ? "No field visits found matching your search"
                            : "No field visit records available"
                        }
                    </Typography>
                </Box>
            )}
        </Card>

            <Dialog
                open={openImageDialog}
                onClose={handleCloseImageDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: theme.palette.background.paper,
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="div">
                        Image view
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseImageDialog}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    {selectedImageUrl && (
                        <img 
                            src={selectedImageUrl} 
                            alt="Preview" 
                            style={{ 
                                maxWidth: '100%', 
                                maxHeight: '70vh', 
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }} 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            
            {onAssign && (
                <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Field Visit</DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Assigned To"
                                value={assignForm.assigned_to}
                                onChange={(e) => {
                                    setAssignForm((prev) => ({ ...prev, assigned_to: e.target.value }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => {
                                        if (!selected) {
                                            return (
                                                <Typography component="span" sx={{ color: "text.secondary" }}>
                                                    {loadingAssignUsers ? "Loading users..." : "Select user"}
                                                </Typography>
                                            );
                                        }
                                        const selectedUser = assignUsers.find(
                                            (user) => String(user.id) === String(selected)
                                        );
                                        return selectedUser
                                            ? getUserDisplayName(selectedUser)
                                            : selected;
                                    },
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 320,
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    {loadingAssignUsers ? "Loading users..." : "Select user"}
                                </MenuItem>
                                {assignUsers.map((user) => (
                                    <MenuItem key={user.id} value={String(user.id)}>
                                        {getUserDisplayName(user)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Farmer Name"
                                value={assignForm.farmer_name}
                                onChange={(e) => {
                                    const lettersOnly = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                    setAssignForm((prev) => ({ ...prev, farmer_name: lettersOnly }));
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Farmer Contact"
                                value={assignForm.farmer_contact}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setAssignForm((prev) => ({ ...prev, farmer_contact: digitsOnly }));
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                                error={
                                    String(assignForm.farmer_contact || "").length > 0 &&
                                    !/^\d{10}$/.test(String(assignForm.farmer_contact || ""))
                                }
                                helperText={
                                    String(assignForm.farmer_contact || "").length > 0 &&
                                    !/^\d{10}$/.test(String(assignForm.farmer_contact || ""))
                                        ? "Farmer Contact must be exactly 10 digits"
                                        : undefined
                                }
                            />
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Customer Type"
                                value={assignForm.customer_type}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, customer_type: e.target.value }))
                                }
                            >
                                <MenuItem value="" disabled>
                                    Select customer type
                                </MenuItem>
                                {CUSTOMER_TYPE_OPTIONS.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Country"
                                value={assignForm.country}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        country: value,
                                        state: "",
                                        district: "",
                                        tehsil: "",
                                    }));
                                    setAssignStates([]);
                                    setAssignDistricts([]);
                                    setAssignTalukas([]);
                                    if (value) fetchAssignStates(value);
                                }}
                            />
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="State"
                                value={assignForm.state}
                                onChange={(e) => {
                                    const selectedState = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        state: selectedState,
                                        district: "",
                                        tehsil: "",
                                    }));
                                    setAssignDistricts([]);
                                    setAssignTalukas([]);
                                    fetchAssignDistricts(selectedState);
                                }}
                            >
                                {assignStates.length > 0 ? (
                                    assignStates.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a country first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="District"
                                value={assignForm.district}
                                onChange={(e) => {
                                    const selectedDistrict = e.target.value;
                                    setAssignForm((prev) => ({
                                        ...prev,
                                        district: selectedDistrict,
                                        tehsil: "",
                                    }));
                                    setAssignTalukas([]);
                                    fetchAssignTalukas(selectedDistrict);
                                }}
                            >
                                {assignDistricts.length > 0 ? (
                                    assignDistricts.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a state first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                required
                                label="Tahshil"
                                value={assignForm.tehsil}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, tehsil: e.target.value }))
                                }
                            >
                                {assignTalukas.length > 0 ? (
                                    assignTalukas.map((item) => (
                                        <MenuItem key={item.id} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Select a district first</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Village"
                                value={assignForm.village}
                                onChange={(e) =>
                                    setAssignForm((prev) => ({ ...prev, village: e.target.value }))
                                }
                            />
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label="Pincode"
                                value={assignForm.pincode}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    setAssignForm((prev) => ({ ...prev, pincode: digitsOnly }));
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                error={
                                    String(assignForm.pincode || "").length > 0 &&
                                    !/^\d{6}$/.test(String(assignForm.pincode || ""))
                                }
                                helperText={
                                    String(assignForm.pincode || "").length > 0 &&
                                    !/^\d{6}$/.test(String(assignForm.pincode || ""))
                                        ? "Pincode must be exactly 6 digits"
                                        : undefined
                                }
                            />
                            {[
                                ["visit_date", "Visit Date", "date"],
                                ["visit_purpose", "Visit Purpose", "text"],
                                ["remarks", "Remarks", "text"],
                            ].map(([field, label, type]) => (
                                <TextField
                                    key={field}
                                    fullWidth
                                    size="small"
                                    required={field !== "remarks"}
                                    label={label}
                                    type={type}
                                    value={assignForm[field]}
                                    onChange={(e) =>
                                        setAssignForm((prev) => ({ ...prev, [field]: e.target.value }))
                                    }
                                    InputLabelProps={type === "date" ? { shrink: true } : undefined}
                                    inputProps={
                                        field === "visit_date"
                                            ? { min: new Date().toISOString().slice(0, 10) }
                                            : undefined
                                    }
                                />
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleAssignSubmit}
                            disabled={
                                assignSubmitting ||
                                !assignForm.assigned_to ||
                                !String(assignForm.farmer_name || "").trim() ||
                                !/^\d{10}$/.test(String(assignForm.farmer_contact || "").trim()) ||
                                !String(assignForm.customer_type || "").trim() ||
                                !String(assignForm.country || "").trim() ||
                                !String(assignForm.state || "").trim() ||
                                !String(assignForm.district || "").trim() ||
                                !String(assignForm.tehsil || "").trim() ||
                                !String(assignForm.village || "").trim() ||
                                !/^\d{6}$/.test(String(assignForm.pincode || "").trim()) ||
                                !String(assignForm.visit_date || "").trim() ||
                                !String(assignForm.visit_purpose || "").trim()
                            }
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                            {assignSubmitting ? "Assigning..." : "Assign"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

         <AppPaginationBar
                    pagination={viewMode === "calendar" ? null : pagination}
                    onPageChange={onPageChange}
                    itemLabel="field visits"
            />
        </Box>
    );
};

export default FieldVisitTable;

